import { AlertCircle, AlertTriangle } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import type { DuplicateMatch } from "@/lib/duplicate-detection";

const RELATIONSHIP_EMOJI: Record<string, string> = {
  Partner: "💕",
  Parent: "🏠",
  Child: "👶",
  Sibling: "👫",
  Friend: "🤝",
  Colleague: "💼",
  Other: "✨",
};

interface DuplicateContactWarningProps {
  matches: DuplicateMatch[];
  onContinue: () => void;
  onViewContact: (contact: DuplicateMatch["contact"]) => void;
  onGoBack: () => void;
}

const DuplicateContactWarning = ({
  matches,
  onContinue,
  onViewContact,
  onGoBack,
}: DuplicateContactWarningProps) => {
  if (matches.length === 0) return null;

  const highestConfidence = matches.some((m) => m.confidence === "high")
    ? "high"
    : "medium";

  const isHigh = highestConfidence === "high";

  return (
    <div
      className="rounded-xl p-4 animate-fade-in"
      style={{
        background: isHigh ? "#FEF2F2" : "#FEF9EE",
        borderLeft: `3px solid ${isHigh ? "#DC2626" : "#C9A84C"}`,
      }}
    >
      {/* Header */}
      <div className="flex items-start gap-2 mb-3">
        {isHigh ? (
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "#DC2626" }} />
        ) : (
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "#C9A84C" }} />
        )}
        <p
          className="text-sm font-bold"
          style={{ color: isHigh ? "#DC2626" : "#C9A84C" }}
        >
          {isHigh
            ? "This looks like it might already be in your Circle."
            : "Someone with a similar name already exists in your Circle."}
        </p>
      </div>

      {/* Matched contacts */}
      <div className="divide-y" style={{ borderColor: "#F3F4F6" }}>
        {matches.map((match) => (
          <div
            key={match.contact.id}
            className="flex items-center justify-between py-2 first:pt-0 last:pb-0"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold truncate" style={{ color: "#1A1A1A" }}>
                {match.contact.full_name}
              </p>
              <p className="text-xs" style={{ color: "#666666" }}>
                {RELATIONSHIP_EMOJI[match.contact.relationship] ?? "✨"}{" "}
                {match.contact.relationship} ·{" "}
                {format(parseISO(match.contact.birthday), "MMM d")}
              </p>
            </div>
            <button
              type="button"
              onClick={() => onViewContact(match.contact)}
              className="text-xs font-bold shrink-0 ml-3 hover:underline"
              style={{ color: "#C9A84C" }}
            >
              View
            </button>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="mt-3 flex flex-col gap-2">
        <Button
          type="button"
          variant="outline"
          className="w-full text-sm whitespace-normal h-auto"
          onClick={onContinue}
        >
          This is a different person — add anyway
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="w-full text-sm text-muted-foreground whitespace-normal h-auto"
          onClick={onGoBack}
        >
          Go back and check
        </Button>
      </div>
    </div>
  );
};

export default DuplicateContactWarning;
